import { useRecoilValue } from 'recoil';
import { AuthAtoms } from 'modules/authentication';
import { Collection, FirestoreService } from 'modules/firebase';
import { useEffect, useRef } from 'react';

import { AudioStatsAtoms } from '../recoil';
import { AudioStatsConstructor } from '../models';
import { orderBy, where } from 'firebase/firestore';

const AudioStatsCollection = new FirestoreService<AudioStats>(
  Collection.AudioStats,
);

/** Progress writes for one track are serialised. Two concurrent writes would
 * otherwise both find no document and both add one — a background flush landing
 * on the same tick as a periodic sync is enough — and the duplicate then shadows
 * the real record, since the listener orders by lastPlayed and readers take the
 * first match. */
const writeQueue = new Map<string, Promise<unknown>>();

function enqueue<T>(key: string, task: () => Promise<T>): Promise<T> {
  const previous = writeQueue.get(key) || Promise.resolve();
  const next = previous.catch(() => undefined).then(task);

  writeQueue.set(key, next);
  /** Drop the entry once it settles, unless a later write has already replaced
   * it, so the map does not grow for the lifetime of the session. */
  next
    .catch(() => undefined)
    .then(() => {
      if (writeQueue.get(key) === next) writeQueue.delete(key);
    });

  return next;
}

export function useAudioStats() {
  const loading = useRecoilValue(AudioStatsAtoms.loading);
  const stats = useRecoilValue(AudioStatsAtoms.stats);
  const user = useRecoilValue(AuthAtoms.user);

  /** The audio player memoises its progress callback per track, so it holds on
   * to whichever closure existed when the track loaded. Reading through refs
   * keeps writes working against current values rather than a snapshot taken
   * before the stats listener or authentication had resolved. */
  const statsRef = useRef(stats);
  const userRef = useRef(user);

  useEffect(() => {
    statsRef.current = stats;
  }, [stats]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  function getStatsByUserId(userId: string) {
    return AudioStatsCollection.getFromServer([
      where('userId', '==', userId),
      orderBy('lastPlayed', 'desc'),
    ]);
  }

  async function findStat(userId: string, trackId: string) {
    const [existing] = await AudioStatsCollection.getFromServer([
      where('userId', '==', userId),
      where('trackId', '==', trackId),
    ]);

    return existing;
  }

  function updatePlayProgress(
    track: AudioInfo,
    percentListened: number,
    secondsListened: number,
  ) {
    const currentUser = userRef.current;
    if (!currentUser) return Promise.resolve();

    return enqueue(`${currentUser.id}_${track.id}`, async () => {
      /** The stats listener has not necessarily resolved yet — on a cold start
       * the player can mount before it does. Treating an empty list as "no
       * document exists" would add a duplicate, so fall back to a direct read.
       * From the server: offline, the cache cannot see a stat another device
       * wrote, so this rejects and the write is skipped. */
      const existing =
        statsRef.current.find(stat => stat.trackId === track.id) ||
        (await findStat(currentUser.id, track.id));

      if (!existing) {
        const stat = new AudioStatsConstructor(
          currentUser.id,
          track.id,
          track.title,
        );
        stat.percentListened = percentListened;
        stat.secondsListened = secondsListened;
        await AudioStatsCollection.add(stat);
        return;
      }

      await AudioStatsCollection.set(existing.id, {
        percentListened,
        secondsListened,
        lastPlayed: +new Date(),
      });
      /** Callers fire progress writes without awaiting them, so a failure —
       * routine on a flaky mobile connection — would otherwise surface as an
       * unhandled rejection. The queue itself still sees the rejection. */
    }).catch(error => console.error('Failed to save audio progress: ', error));
  }

  function onTrackCompleted(track: AudioInfo) {
    return updatePlayProgress(track, 0, 0);
  }

  return {
    stats,
    loading,
    updatePlayProgress,
    onTrackCompleted,
    getStatsByUserId,
  };
}
